<template>
	<div>
		<p class="text-h6 mb-0">{{ nearbyProjects.length > 0 ? "Projects Nearby" : "No Projects Nearby" }}</p>
		<v-list density="compact">
			<div v-if="nearbyProjects.length <= 0" class="text-caption">Select a location below to create a new project.</div>

			<v-list-item v-for="(project, index) in nearbyProjects" :key="project.id || index" class="pa-1" @click="selectProject(project)">
				<template v-slot:prepend>
					<v-chip size="small" color="primary" text-color="white" class="mr-2 full-width-chip">
						{{ project.GeoAddress?.distance ?? "No address" }}
					</v-chip>
				</template>
				<v-tooltip :text="project.name" :disabled="!project.name" location="bottom">
					<template #activator="{ props: tooltipProps }">
						<v-list-item-subtitle v-bind="tooltipProps" :aria-label="project.name">
							{{ project.name }}
						</v-list-item-subtitle>
					</template>
				</v-tooltip>
			</v-list-item>
		</v-list>
	</div>
</template>

<script lang="ts" setup>
	import { ref, onMounted, watch } from "vue";
	import api from "@/api";
	import { calculateDistance, Project, UserLocation } from "@/helpers/location";
	import { logError } from "@/helpers/logError";
	import { useOrganization } from "@/composables/useOrganization";

	const { currentOrganizationDefaultCountry } = useOrganization();

	const props = defineProps<{
		userLocation: UserLocation;
		searchRadius: number;
	}>();

	const emit = defineEmits<{
		(e: "projectSelected", project: Project): void;
	}>();

	const nearbyProjects = ref<Project[]>([]);
	let activeRequestId = 0;

	onMounted(() => {
		getNearbyProjects();
	});

	// Array-of-getters form so Vue compares each value individually instead of
	// the wrapping array's reference — prevents a redundant fetch when the
	// parent replaces `userLocation` with a new object that has the same coords.
	// currentOrganizationDefaultCountry is included because it loads async (undefined
	// until getMe() resolves): if the org country resolves after the initial fetch,
	// re-run so distances recompute in the correct measurement system (LSP-16755).
	watch(
		[
			() => props.userLocation?.latitude,
			() => props.userLocation?.longitude,
			() => props.searchRadius,
			() => currentOrganizationDefaultCountry.value
		],
		() => {
			getNearbyProjects();
		}
	);

	async function selectProject(project: Project) {
		emit("projectSelected", project);
	}

	async function getNearbyProjects() {
		// Increment FIRST so an in-flight call started under the previous
		// userLocation is reliably marked stale even when this invocation
		// early-returns. Otherwise that in-flight call could still resolve,
		// pass the stale check, and dereference a now-falsy `props.userLocation`
		// in the distance loop outside the try/catch.
		const requestId = ++activeRequestId;
		if (!props.userLocation) return;

		let results: Project[] = [];
		try {
			results = ((await api.getNearbyProjects(props.userLocation.latitude, props.userLocation.longitude, props.searchRadius)) ??
				[]) as Project[];
		} catch (error) {
			// LSP-15714: never let a failed nearby-projects lookup crash the host page.
			// Fall through to the empty state so the user sees "No Projects Nearby".
			if (requestId !== activeRequestId) return;
			logError("NearbyProjects: failed to load nearby projects", error, {
				radiusInMeters: props.searchRadius
			});
			nearbyProjects.value = [];
			return;
		}

		// Drop the result if a newer call has started since we awaited the API.
		if (requestId !== activeRequestId) return;

		for (const project of results) {
			if (!project.GeoAddress) continue;
			project.GeoAddress.distance = calculateDistance(
				props.userLocation,
				{
					latitude: project.GeoAddress.latitude,
					longitude: project.GeoAddress.longitude
				},
				currentOrganizationDefaultCountry.value
			);
		}

		results.sort((a, b) => {
			const aDistance = parseFloat(a.GeoAddress?.distance?.replace(/[^\d.]/g, "") || "0");
			const bDistance = parseFloat(b.GeoAddress?.distance?.replace(/[^\d.]/g, "") || "0");
			return aDistance - bDistance;
		});

		nearbyProjects.value = results;
	}
</script>
